import { InferenceAPI, useTokenAndOrgId } from './utils'
import {
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { getUserSort } from '@/pages/DashboardUserManagement/types'
import { UsersSortKeys } from '@/pages/DashboardUserManagement/types'
import { processPaginatedData } from './utils'
import { toast } from 'react-hot-toast'
import {
  CreateOrgInviteRequest,
  GetOrgMembersRequest,
  InviteUserRequest,
  UpdateOrgUserArgs,
  UserOrganization,
} from './inference.gen'

// Utility type to make specific keys optional
type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>

export const LIST_USERS_PAGE_SIZE = 50

export const getOrgUsers = async (
  args: Optional<
    GetOrgMembersRequest,
    'page_number' | 'page_size' | 'sort_by' | 'sort_direction'
  >
) => {
  const result = await InferenceAPI.getOrgUsers({
    request: {
      ...args,
      page_size: args.page_size || LIST_USERS_PAGE_SIZE,
      page_number: args.page_number || 1,
      sort_by: args.sort_by || 'created_at',
      sort_direction: args.sort_direction || 'desc',
    },
  })

  return result
}

export const useGetOrgUsers = (
  status: string,
  searchText = '',
  sort: UsersSortKeys = UsersSortKeys.CREATED_AT_DESC
) => {
  const { orgId, token } = useTokenAndOrgId()
  const fetchOrgUsers = async ({ pageParam = 1 }) => {
    const result = await getOrgUsers({
      status,
      page_size: LIST_USERS_PAGE_SIZE,
      page_number: pageParam,
      search_text: searchText,
      sort_by: getUserSort(sort).sortBy,
      sort_direction: getUserSort(sort).sortDirection,
    })

    return result
  }

  return useInfiniteQuery({
    queryKey: ['org-users', orgId, status, searchText, sort],
    queryFn: fetchOrgUsers,
    enabled: !!orgId && !!token,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.users.length === LIST_USERS_PAGE_SIZE
        ? pages.length + 1
        : undefined
    },
    select: (data) => {
      const processedData = processPaginatedData<UserOrganization>(data.pages)
      return {
        allUsers: processedData.allInstances,
        total: processedData.total,
      }
    },
    initialPageParam: 1,
  })
}

export const useCreateOrgInvite = () => {
  const { orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateOrgInviteRequest) => {
      const result = await InferenceAPI.createOrgInvite({
        request: data,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-users', orgId],
        exact: false,
      })
      toast.success('Invites sent successfully')

      return result
    },
  })
}

export const useResendOrgInvite = () => {
  const { orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateOrgInviteRequest) => {
      const result = await InferenceAPI.createOrgInvite({
        request: data,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-users', orgId],
        exact: false,
      })
      toast.success('Invitation resent successfully')

      return result
    },
  })
}

export const useCreateOrgInviteBatch = () => {
  const { orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: InviteUserRequest[]) => {
      const result = await InferenceAPI.createOrgInviteBatch({
        invites: data,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-users', orgId],
        exact: false,
      })

      toast.success('Invites sent successfully')

      return result
    },
  })
}

export const useRemoveOrgUser = () => {
  const { orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userId: number) => {
      const result = await InferenceAPI.deleteOrgUser({
        user_id: userId,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-users', orgId],
        exact: false,
      })
      toast.success('User removed successfully')

      return result
    },
  })
}

export const useUpdateOrgUserRole = () => {
  const { orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateOrgUserArgs) => {
      const result = await InferenceAPI.updateOrgUser({
        ...data,
      })

      queryClient.invalidateQueries({
        queryKey: ['org-users', orgId],
        exact: false,
      })
      toast.success('User role updated successfully')

      return result
    },
  })
}
